/**
 * 「跟我练」100 节课的目录与谱面。
 *
 * ## 数据从哪来
 *
 * 课纲与谱面都是**构建期**产物，不经过数据库：
 *   · 谱面按课纲写在 `scripts/practice-course-data.mts`（音 + 时值结构），
 *     由 `scripts/build-practice-course.mts` 渲染成 `public/lessons/*.spmn`，
 *     生成时逐小节校验时值是否等于拍号；
 *   · 目录是 `public/lessons/course.json`（阶段 → 课 → 文件）。
 *
 * 为什么不进 `library_items`：这 100 首不是"曲目"，是**按顺序走的教学序列**。
 * 曲库那套按体裁/难度筛选、按 id 取谱的模型套上去，只会多出一个"分类=练习课程"
 * 的废物筛选桶，还得为它配审核与隐藏逻辑。课程要的是"第 12 课之后是第 13 课"，
 * 顺序才是主键。
 *
 * ## 为什么课纲不在这里手写一遍
 *
 * 卡的标题与达标要求来自 `course.json`，与谱面同一次生成 —— 两边不可能对不上。
 * 在这里再抄一份课纲，改课纲时就必然有一处漏改。
 */

/** 一节练习课 */
export type CourseLesson = {
	no: number
	title: string
	/** 达标要求：课纲原话，写在卡片的第二行 */
	goal: string
	/** `public/lessons/` 下的文件名 */
	file: string
	bars: number
	voices: number
}

/** 一个阶段（10~20 课一组，对应课纲的七个阶段） */
export type CourseStage = {
	no: number
	title: string
	/** 形如「第 1–10 课」 */
	range: string
	goal: string
	lessons: CourseLesson[]
}

export type CourseCatalog = {
	version: number
	total: number
	generatedAt: string
	note: string
	stages: CourseStage[]
}

/**
 * 取课程目录。
 *
 * 走**相对路径**（与 `examples.ts` 的取谱同一套约定）：生产是 `--base=./` +
 * 哈希路由，相对路径始终对着 index.html 所在目录解析，dev 与子目录部署都对。
 */
export async function fetchCourseCatalog(): Promise<CourseCatalog> {
	const res = await fetch('./lessons/course.json')
	if (!res.ok) throw new Error(`课程目录读取失败（${res.status}）`)
	const data = (await res.json()) as CourseCatalog
	if (!data || !Array.isArray(data.stages)) throw new Error('课程目录格式不对')
	return data
}

/** 取某一课的谱面正文 */
export async function fetchLessonScore(lesson: CourseLesson): Promise<string> {
	const res = await fetch(`./lessons/${encodeURIComponent(lesson.file)}`)
	if (!res.ok) throw new Error(`第 ${lesson.no} 课读取失败（${res.status}）`)
	return await res.text()
}
